import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../../api/axios';
import { useLoading } from '../../../../components/LoadingProvider';
import useAuth from '../../../../hooks/useAuth';
import { showSuccess, showError } from '../../../../utils/toast';
import { getIndStates } from '../../../../utils/common';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useDebouncedCallback } from 'use-debounce';

export default function DoctorAppointmentGeneralPrescriptionPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    document.title = `Varosa - Doctor Appointment Prescription No ${id}`;
    const { setIsLoading } = useLoading();
    const { userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const [ appointmentPrescriptionData, setAppointmentPrescriptionData ] = useState(null);
    const [ medsProduct, setMedsProduct ] = useState([]);
    const [ oldAppointments, setOldAppointments ] = useState([]);
    const prefixOfName = [ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ];
    const [ accordionStatus, setAccordionStatus ] = useState({
        one: true,
    });
    const formPatientRef = useRef(null);
    const formRef = useRef(null);
    const formMedicineRef = useRef(null);
    const indStates = getIndStates();
    const medsFrequency = [
        'Once a day',
        'Twice daily',
        'Thrice daily',
        'Four times a day',
        'Five times a day',
        'Six times a day',
        'Once',
        'HS',
        'Once a week',
        'Twice a week',
        'Thrice a week',
        'Once a month',
        'Twice a month',
        'Thrice a month',
        'Every hour',
        'Every six hours',
        'Alternate day',
        'Once in every six month'
    ];
    const medsSchedule = [
        'Morning', 'Afternoon', 'Evening', 'Night'
    ];
    const medsScheduleArr = [{
            value: 'Morning',
            label: 'Morning'
        },
        {
            value: 'Afternoon',
            label: 'Afternoon'
        },
        {
            value: 'Evening',
            label: 'Evening'
        },
        {
            value: 'Night',
            label: 'Night'
        }
    ];
    const medsRoute = [
        'Oral Route',
        'Puff',
        'Enteral Route',
        'Intradermal',
        'Epidural',
        'Topical Route',
        'Inhalation Route',
        'Eye Route',
        'Ear Route',
        'Eye/Ear Route',
        'Nasal Route',
        'Rectal Route',
        'Injectable Route',
        'Sub-Cutaneous Route',
    ];
    const [ prescriptionFormData, setPrescriptionFormData ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_line2: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        chief_complaints: '',
        history_points: '',
        lab_findings: '',
        suggested_investigations: '',
        special_instructions: '',
        diagnosis: '',
        med_rx: '',
        remarks: '',
        med_rx_id: null,
        med_product: {},
        medicine_name_manually: '',
        med_dosage: '1',
        med_route: 'Oral Route',
        med_frequency: 'Once a day',
        med_schedule: 'Morning',
        med_schedule_arr: [{
            value: 'Morning',
            label: 'Morning'
        }],
        med_instruction: 'As directed',
        med_duration: 'Till review',
        med_rx_arr: [],
        is_completed: 'Yes',
        prescription_type: 'general',
    });
    const [ prescriptionFormError, setPrescriptionFormError ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        chief_complaints: '',
        history_points: '',
        lab_findings: '',
        suggested_investigations: '',
        special_instructions: '',
        diagnosis: '',
        med_rx: '',
        remarks: '',
        is_completed: '',
    });
    useEffect(() => {
        fetchAppointmentPrescription();
    }, []);
    const fetchAppointmentPrescription = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/general-prescription`);
            if (response?.data?.result === 'success') {
                setAppointmentPrescriptionData(response?.data?.data);
                setOldAppointments(response?.data?.data.old_appointments);
                let found_state = response?.data?.data?.patient?.state || '';
                if(found_state.length > 2){
                    let ind_state = indStates.find(s => {
                        return found_state.replaceAll(' ', '').toLocaleLowerCase() === s.name.replaceAll(' ', '').toLocaleLowerCase();
                    });
                    if(ind_state){
                        found_state = ind_state.key;
                    }
                }
                setPrescriptionFormData({
                    patient_name: response?.data?.data?.patient?.name || '',
                    patient_name_prefix: response?.data?.data?.patient?.name_prefix || '',
                    patient_dob: response?.data?.data?.patient?.dob || '',
                    patient_gender: response?.data?.data?.patient?.gender || '',
                    patient_address_line1: response?.data?.data?.patient?.address_line1 || '',
                    patient_address_line2: response?.data?.data?.patient?.address_line2 || '',
                    patient_address_district: response?.data?.data?.patient?.district || '',
                    patient_address_state: found_state,
                    patient_address_pincode: response?.data?.data?.patient?.pincode || '',
                    patient_address_country: response?.data?.data?.patient?.country || '',
                    chief_complaints: response?.data?.data?.appointment_prescription?.chief_complaints || '',
                    history_points: response?.data?.data?.appointment_prescription?.history_points || '',
                    lab_findings: response?.data?.data?.appointment_prescription?.lab_findings || '',
                    suggested_investigations: response?.data?.data?.appointment_prescription?.suggested_investigations || '',
                    special_instructions: response?.data?.data?.appointment_prescription?.special_instructions || '',
                    diagnosis: response?.data?.data?.appointment_prescription?.diagnosis || '',
                    med_rx: response?.data?.data?.appointment_prescription?.med_rx || '',
                    med_rx_arr: response?.data?.data?.appointment_prescription?.med_rx_products || [],
                    remarks: response?.data?.data?.appointment_prescription?.remarks || '',
                    med_rx_id: null,
                    medicine_name_manually: '',
                    med_product: {},
                    med_dosage: '1',
                    med_route: 'Oral Route',
                    med_frequency: 'Once a day',
                    med_schedule: 'Morning',
                    med_schedule_arr: [{
                        value: 'Morning',
                        label: 'Morning'
                    }],
                    med_instruction: 'As directed',
                    med_duration: 'Till review',
                    is_completed: 'Yes',
                    prescription_type: 'general',
                });
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const filterMeds = useDebouncedCallback(async (inputValue, callback) => {
        if (!inputValue) {
            setMedsProduct([]);
        }
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/medicine-products?name=${inputValue}`);
            if (response?.data?.result === 'success') {
                setMedsProduct(response?.data?.data?.map(item => ({
                    value: item?.id,
                    label: item?.name
                })));
                callback(response?.data?.data?.map(item => ({
                    value: item?.id,
                    label: item?.name
                })));
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
            setMedsProduct([]);
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
            setMedsProduct([]);
        } finally {
            setIsLoading(false);
        }
    }, 1000);
    const loadMedsOptions = (inputValue, callback) => {
        filterMeds(inputValue, callback);
        // setTimeout(() => {
        //     callback(medsProduct);
        // }, 1500);
    };
    const addRxFormData = () => {
        if (!prescriptionFormData?.med_product?.value && !prescriptionFormData?.medicine_name_manually) {
            setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Medicine product is required' });
            return;
        }
        if (!prescriptionFormData?.med_dosage) {
            setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Dosage is required' });
            return;
        }
        if (!prescriptionFormData?.med_route) {
            setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Route is required' });
            return;
        }
        if (!prescriptionFormData?.med_frequency) {
            setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Frequency is required' });
            return;
        }
        // if (!prescriptionFormData?.med_schedule) {
        //     setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Schedule is required' });
        //     return;
        // }
        if (prescriptionFormData?.med_schedule_arr?.length === 0) {
            setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Schedule is required' });
            return;
        }
        if (!prescriptionFormData?.med_instruction) {
            setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Instruction is required' });
            return;
        }
        if (!prescriptionFormData?.med_duration) {
            setPrescriptionFormError({ ...prescriptionFormError, med_rx: 'Duration is required' });
            return;
        }
        if (!prescriptionFormData?.is_completed) {
            setPrescriptionFormError({ ...prescriptionFormError, is_completed: 'Appointment Completed is required' });
            return;
        }
        setPrescriptionFormError({ ...prescriptionFormError, med_rx: '' });
        let med_product_data = {};
        if(prescriptionFormData?.med_product?.value){
            if(prescriptionFormData?.med_product?.value !== '-1'){
                med_product_data = prescriptionFormData?.med_product;
            }else{
                med_product_data = {
                    label: prescriptionFormData?.medicine_name_manually,
                    value: '-1',
                }
            }
        }else{
            med_product_data = {
                label: prescriptionFormData?.medicine_name_manually,
                value: '-1',
            }
        }
        let data = {
            product: med_product_data,
            dosage: prescriptionFormData?.med_dosage, 
            route: prescriptionFormData?.med_route, 
            frequency: prescriptionFormData?.med_frequency, 
            schedule: prescriptionFormData?.med_schedule,
            schedule_arr: prescriptionFormData?.med_schedule_arr,
            instruction: prescriptionFormData?.med_instruction,
            duration: prescriptionFormData?.med_duration 
        };
        if(prescriptionFormData?.med_rx_id !== null){
            let arr = [...prescriptionFormData.med_rx_arr];
            data.id = arr[prescriptionFormData?.med_rx_id].id || null;
            arr[prescriptionFormData?.med_rx_id] = data;
            setPrescriptionFormData(prev => ({
                ...prev,
                med_rx_arr: arr,
                med_rx_id: null,
                medicine_name_manually: '',
                med_product: {}, 
                med_dosage: '1', 
                med_route: 'Oral Route', 
                med_frequency: 'Once a day', 
                med_schedule: 'Morning', 
                med_schedule_arr: [{
                    value: 'Morning',
                    label: 'Morning'
                }],
                med_instruction: 'As directed', 
                med_duration: 'Till review'
            }));
        } else {
            setPrescriptionFormData(prev => ({
                ...prev,
                med_rx_arr: [...prev.med_rx_arr, data],
                med_rx_id: null,
                medicine_name_manually: '',
                med_product: {}, 
                med_dosage: '1', 
                med_route: 'Oral Route', 
                med_frequency: 'Once a day', 
                med_schedule: 'Morning', 
                med_schedule_arr: [{
                    value: 'Morning',
                    label: 'Morning'
                }],
                med_instruction: 'As directed', 
                med_duration: 'Till review'
            }));
        }
        const input = document.getElementById('react-select-3-input');
        if (input) input.value = '';
        const placeholder = document.getElementById('react-select-3-placeholder');
        if (placeholder) placeholder.innerHTML = 'Select...';
        document.querySelectorAll('[class*="-singleValue"]').forEach(element => {
            element.innerHTML = 'Select...';
        });
    }
    const editRxFormData = (index) => {
        setPrescriptionFormData({ ...prescriptionFormData, 
            med_rx_id: index,
            med_product: prescriptionFormData?.med_rx_arr[index]?.product,
            medicine_name_manually: prescriptionFormData?.med_rx_arr[index]?.product?.value == '-1' ? prescriptionFormData?.med_rx_arr[index]?.product?.label : '',
            med_dosage: prescriptionFormData?.med_rx_arr[index]?.dosage,
            med_route: prescriptionFormData?.med_rx_arr[index]?.route,
            med_frequency: prescriptionFormData?.med_rx_arr[index]?.frequency,
            med_schedule: prescriptionFormData?.med_rx_arr[index]?.schedule,
            med_schedule_arr: prescriptionFormData?.med_rx_arr[index]?.schedule_arr,
            med_instruction: prescriptionFormData?.med_rx_arr[index]?.instruction,
            med_duration: prescriptionFormData?.med_rx_arr[index]?.duration
        });
        if(prescriptionFormData?.med_rx_arr[index]?.product?.value !== '-1'){
            let product_label = prescriptionFormData?.med_rx_arr[index]?.product?.label;
            const input = document.getElementById('react-select-3-input');
            if (input) input.value = product_label || '';
            const placeholderEl = document.getElementById('react-select-3-placeholder');
            if (placeholderEl) placeholderEl.innerHTML = product_label || 'Select...';
            document.querySelectorAll('[class*="-singleValue"]').forEach(element => {
                element.innerHTML = product_label || 'Select...';
            })
        }
    }
    const removeRxFormData = (index) => {
        setPrescriptionFormData({ ...prescriptionFormData, med_rx_arr: [...prescriptionFormData?.med_rx_arr.filter((item, i) => i !== index)] });
    }
    const handleAppointmentDateChange = async (e) => {
        const { name, value } = e.target;
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/old-prescription?type=general&search_date=`+value);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setOldAppointments(response?.data?.data.old_appointments);
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const handlePrescription = async () => {
        try {
            const resetPrescriptionFormError = {
                patient_name: '',
                patient_name_prefix: '',
                patient_dob: '',
                patient_gender: '',
                patient_address_line1: '',
                patient_address_line2: '',
                patient_address_district: '',
                patient_address_state: '',
                patient_address_pincode: '',
                chief_complaints: '',
                history_points: '',
                lab_findings: '',
                suggested_investigations: '',
                special_instructions: '',
                diagnosis: '',
                med_rx: ''
            };
            setPrescriptionFormError(resetPrescriptionFormError);
            if (!prescriptionFormData?.patient_name) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name: 'Patient Name is required' });
                return;
            }
            if (!prescriptionFormData?.patient_name_prefix) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name_prefix: 'Patient Name Prefix is required' });
                return;
            }
            if (!prescriptionFormData?.patient_dob) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_dob: 'Patient DOB is required' });
                return;
            }
            if (!prescriptionFormData?.patient_gender) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_gender: 'Patient Gender is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_line1) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_line1: 'Patient Address line 1 is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_district) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_district: 'Patient District is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_state) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_state: 'Patient State is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_pincode) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_pincode: 'Patient Pincode is required' });
                return;
            }
            if (!prescriptionFormData?.chief_complaints) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, chief_complaints: 'Chief complaints is required' });
                return;
            }
            if (!prescriptionFormData?.history_points) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, history_points: 'History points is required' });
                return;
            }
            if (!prescriptionFormData?.lab_findings) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, lab_findings: 'Lab findings is required' });
                return;
            }
            if (!prescriptionFormData?.suggested_investigations) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, suggested_investigations: 'Suggested investigations is required' });
                return;
            }
            if (!prescriptionFormData?.special_instructions) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, special_instructions: 'Special instructions is required' });
                return;
            }
            if (!prescriptionFormData?.diagnosis) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, diagnosis: 'Diagnosis is required' });
                return;
            }
            if (!prescriptionFormData?.med_rx_arr?.length) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, med_rx: 'Medication Rx is required' });
                return;
            }
            if (!prescriptionFormData?.is_completed) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, is_completed: 'Appointment Completed is required' });
                return;
            }
            setIsLoading(true);
            const response = await api.post(`/${userRole}/appointment/${id}/general-prescription`, prescriptionFormData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                fetchAppointmentPrescription();  
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <>
            <div className="container py-5 login-container appointment-detail">
                <div className="row g-3">
                    <div className="col-8">
                        <h2 className="mb-4">Appointment Prescription No {id}</h2>
                    </div>
                    <div className="col-4 text-end">
                        <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                    </div>
                    <hr />
                </div>
                <div className="row g-3">
                    <div className="col-md-12">
                        <div className="accordion">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingOne" 
                                    onClick={() => setAccordionStatus({ ...accordionStatus, one: !accordionStatus.one })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.one ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne" aria-expanded="true" aria-controls="panelsStayOpen-collapseOne">
                                        Previous Appointments
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseOne" className={`accordion-collapse collapse ${accordionStatus.one ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingOne">
                                    <div className="accordion-body">
                                        <div className="row">
                                            <div className="col-12">
                                                <label className="form-label">Select Appointment Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control py-3 border-primary"
                                                    name="appointment_date"
                                                    onChange={handleAppointmentDateChange}
                                                    max={new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                            <hr className="mt-4 mb-3" />
                                            <div className="col-12">
                                                {oldAppointments.length > 0 ?
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col" style={{ width: '10%' }}>IDs</th>
                                                            <th scope="col" style={{ width: '30%' }}>Appointment Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Patient Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {oldAppointments?.map((item, index) => (<tr key={index}>
                                                            <th scope="row" style={{ width: '10%' }}>{item.appointment.id}</th>
                                                            <td style={{ width: '30%' }}>
                                                                Date & Time: <b>{item.appointment.appointment_date_time}</b><br />
                                                                Appointment Completed: <b>{item.appointment.is_completed}</b>
                                                            </td>
                                                            <td style={{ width: '30%' }}>
                                                                Name: <b>{item.appointment.patient_name}</b><br />
                                                                Age: <b>{item.appointment.patient_age} Yrs</b><br />
                                                                Gender: <b>{item.appointment.patient_gender.toUpperCase()}</b><br />
                                                                Address: <b>{item.appointment.patient_address}</b>
                                                            </td>
                                                            <td style={{ width: '30%' }}>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-primary btn-sm mt-1 mb-1 me-1"
                                                                    onClick={(e) => {
                                                                        let found_state = item.patient.state || '';
                                                                        if(found_state.length > 2){
                                                                            let ind_state = indStates.find(s => {
                                                                                return found_state.replaceAll(' ', '').toLocaleLowerCase() === s.name.replaceAll(' ', '').toLocaleLowerCase();
                                                                            });
                                                                            if(ind_state){
                                                                                found_state = ind_state.key;
                                                                            }
                                                                        }
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            patient_name_prefix: item.patient.name_prefix,
                                                                            patient_name: item.patient.name,
                                                                            patient_dob: item.patient.dob,
                                                                            patient_gender: item.patient.gender,
                                                                            patient_address_line1: item.patient.address_line1,
                                                                            patient_address_line2: item.patient.address_line2,
                                                                            patient_address_district: item.patient.district,
                                                                            patient_address_state: found_state,
                                                                            patient_address_pincode: item.patient.pincode,
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            patient_name_prefix: '',
                                                                            patient_name: '',
                                                                            patient_dob: '',
                                                                            patient_gender: '',
                                                                            patient_address_line1: '',
                                                                            patient_address_district: '',
                                                                            patient_address_state: '',
                                                                            patient_address_pincode: '',
                                                                        });
                                                                        formPatientRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Patient Data</button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-secondary btn-sm mt-1 mb-1 me-1" 
                                                                    onClick={(e) => {
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            chief_complaints: item.appointment_prescription.chief_complaints,
                                                                            history_points: item.appointment_prescription.history_points,
                                                                            lab_findings: item.appointment_prescription.lab_findings,
                                                                            diagnosis: item.appointment_prescription.diagnosis,
                                                                            suggested_investigations: item.appointment_prescription.suggested_investigations,
                                                                            special_instructions: item.appointment_prescription.special_instructions,
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            chief_complaints: '',
                                                                            history_points: '',
                                                                            lab_findings: '',
                                                                            diagnosis: '',
                                                                            suggested_investigations: '',
                                                                            special_instructions: '',
                                                                        });
                                                                        formRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Prescription Form Data</button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-success btn-sm mt-1 mb-1 me-1" 
                                                                    onClick={(e) => {
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            med_rx: item.appointment_prescription?.med_rx || '',
                                                                            med_rx_arr: item.appointment_prescription?.med_rx_products || [],
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            med_rx: '',
                                                                        });
                                                                        formMedicineRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Medicine List Data</button>
                                                            </td>
                                                        </tr>))}
                                                    </tbody>
                                                </table> : <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                                    Previous Appointments not found!
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12" ref={formPatientRef}>
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title">Prescription Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Patient ID:</strong> {appointmentPrescriptionData?.appointment?.patient_id}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Consultation ID:</strong> {appointmentPrescriptionData?.appointment?.id}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Consultation Date:</strong> {appointmentPrescriptionData?.appointment?.appointment_date_time}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Doctor Name:</strong> {appointmentPrescriptionData?.appointment?.doctor_name}
                                        </p>
                                    </div>
                                    <hr />
                                    <div className="col-md-6">
                                        <label className="form-label">Name</label>
                                        <div className="input-group mb-3">
                                            <select 
                                                className="form-select py-3 border-primary"
                                                name="namePrefix"
                                                value={prescriptionFormData?.patient_name_prefix}
                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name_prefix: e.target.value })}
                                                style={{ maxWidth: '120px' }}
                                            >
                                                <option value="">Choose...</option>
                                                {prefixOfName.map((prefix) => (
                                                    <option key={prefix} value={prefix}>{prefix}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="text"
                                                className="form-control py-3 border-primary"
                                                name="name"
                                                value={prescriptionFormData?.patient_name}
                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        {prescriptionFormError.patient_name && <div className="invalid-feedback d-block">Please enter a valid name.</div>}
                                        {prescriptionFormError.patient_name_prefix && <div className="invalid-feedback d-block">{prescriptionFormError.patient_name_prefix}</div>}
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">DOB</label>
                                        <input
                                            type="date"
                                            className="form-control py-3 border-primary"
                                            name="dob"
                                            value={prescriptionFormData?.patient_dob}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_dob: e.target.value })}
                                            required
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                        {prescriptionFormError.patient_dob && <div className="invalid-feedback d-block">Please enter a valid DOB.</div>}
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Gender</label>
                                        <select
                                            className="form-select py-3 border-primary"
                                            name="gender"
                                            value={prescriptionFormData?.patient_gender}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_gender: e.target.value })}
                                            required
                                        >
                                            <option value="">Choose...</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        {prescriptionFormError.patient_gender && <div className="invalid-feedback d-block">Please select a valid gender.</div>}
                                    </div>

                                    {/* Address Fields */}
                                    <div className="col-md-6">
                                        <label className="form-label">Address Line 1</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_line1"
                                            value={prescriptionFormData?.patient_address_line1}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line1: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_line1 && <div className="invalid-feedback d-block">Please enter a valid address.</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Address Line 2 (optional)</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_line2"
                                            value={prescriptionFormData?.patient_address_line2}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line2: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">District</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_district"
                                            value={prescriptionFormData?.patient_address_district}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_district: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_district && <div className="invalid-feedback d-block">Please enter a valid district.</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">State</label>
                                        <select
                                            className="form-select py-3 border-primary"
                                            name="address.state"
                                            value={prescriptionFormData?.patient_address_state}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_state: e.target.value })}
                                            required
                                        >
                                            <option value="">Choose...</option>
                                            {indStates.map((val) => (<option key={val.key} value={val.key}>{val.name}</option>))}
                                        </select>
                                        {prescriptionFormError.patient_address_state && <div className="invalid-feedback d-block">Please select a valid state.</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Pincode</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_pincode"
                                            value={prescriptionFormData?.patient_address_pincode}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_pincode: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_pincode && <div className="invalid-feedback d-block">Please enter a valid pincode (6-digits).</div>}
                                    </div>
                                    <hr />
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <b>Support & Helpline (10am-6pm):</b> +91 8240112175 <br /><b>Office Number:</b> +91 3482329183 
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12" ref={formRef}>
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title">Prescription Form</h5>
                            </div>
                            <div className="card-body">
                                <form>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="chief_complaints" className="form-label">CHIEF COMPLAINTS</label>
                                                <textarea className="form-control" id="chief_complaints" rows="3" value={prescriptionFormData?.chief_complaints} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, chief_complaints: e.target.value })}></textarea>
                                                {prescriptionFormError?.chief_complaints && <div className="text-danger">{prescriptionFormError?.chief_complaints}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="history_points" className="form-label">RELEVANT POINTS FROM HISTORY</label>
                                                <textarea className="form-control" id="history_points" rows="3" value={prescriptionFormData?.history_points} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, history_points: e.target.value })}></textarea>
                                                {prescriptionFormError?.history_points && <div className="text-danger">{prescriptionFormError?.history_points}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="lab_findings" className="form-label">EXAMINATION/LAB FINDING</label>
                                                <textarea className="form-control" id="lab_findings" rows="3" value={prescriptionFormData?.lab_findings} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, lab_findings: e.target.value })}></textarea>
                                                {prescriptionFormError?.lab_findings && <div className="text-danger">{prescriptionFormError?.lab_findings}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="diagnosis" className="form-label">DIAGNOSIS OR PROVISIONAL DIAGNOSIS</label>
                                                <textarea className="form-control" id="diagnosis" rows="3" value={prescriptionFormData?.diagnosis} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, diagnosis: e.target.value })}></textarea>
                                                {prescriptionFormError?.diagnosis && <div className="text-danger">{prescriptionFormError?.diagnosis}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="suggested_investigations" className="form-label">SUGGESTED INVESTIGATIONS</label>
                                                <textarea className="form-control" id="suggested_investigations" rows="3" value={prescriptionFormData?.suggested_investigations} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, suggested_investigations: e.target.value })}></textarea>
                                                {prescriptionFormError?.suggested_investigations && <div className="text-danger">{prescriptionFormError?.suggested_investigations}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="special_instructions" className="form-label">SPECIAL INSTRUCTIONS</label>
                                                <textarea className="form-control" id="special_instructions" rows="3" value={prescriptionFormData?.special_instructions} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, special_instructions: e.target.value })}></textarea>
                                                {prescriptionFormError?.special_instructions && <div className="text-danger">{prescriptionFormError?.special_instructions}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-12" ref={formMedicineRef}>
                                            <div className="mb-3">
                                                <label htmlFor="med_rx" className="form-label">Rx</label>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered">
                                                        <thead>
                                                            <tr>
                                                                <th scope="col">Medicine Name</th>
                                                                <th scope="col">Dosage</th>
                                                                <th scope="col">Route</th>
                                                                <th scope="col">Frequency</th>
                                                                <th scope="col">Schedule</th>
                                                                <th scope="col">Instruction</th>
                                                                <th scope="col">Duration</th>
                                                                <th scope="col">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {prescriptionFormData?.med_rx_arr?.map((item, index) => (
                                                                <tr key={index}>
                                                                    <td>{item?.product?.label}</td>
                                                                    <td>{item?.dosage}</td>
                                                                    <td>{item?.route}</td>
                                                                    <td>{item?.frequency}</td>
                                                                    <td>{(item?.schedule_arr ? item?.schedule_arr.map(i => i.value).join(', ') : item?.schedule)}</td>
                                                                    <td>{item?.instruction}</td>
                                                                    <td>{item?.duration}</td>
                                                                    <td>
                                                                        <button type="button" className="btn btn-warning btn-sm mt-1 mb-1 me-1" onClick={() => { editRxFormData(index) }}>Edit</button>
                                                                        <button type="button" className="btn btn-danger btn-sm mt-1 mb-1 me-1" onClick={() => { removeRxFormData(index) }}>Remove</button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {/* <textarea className="form-control" id="med_rx" rows="15" value={prescriptionFormData?.med_rx} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_rx: e.target.value })}></textarea> */}
                                            </div>
                                            <div className="card mb-3">
                                                <div className="card-header">
                                                    <h5 className="card-title">{prescriptionFormData?.med_rx_id !== null ? 'Edit' : 'Add'} Medicine</h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row rounded border-2 border-danger">
                                                        <div className="col-md-4 mb-4">
                                                            <label className="form-label">Select Medicine</label>
                                                            <AsyncSelect 
                                                                cacheOptions={medsProduct} 
                                                                defaultOptions={medsProduct} 
                                                                isClearable={true} 
                                                                loadOptions={loadMedsOptions} 
                                                                onChange={(selectedOption) => setPrescriptionFormData({ ...prescriptionFormData, med_product: selectedOption })} 
                                                                value={prescriptionFormData?.med_product}
                                                            />
                                                            <p style={{width: '100%', textAlign: 'center', borderBottom: '1px solid #dc3545', lineHeight: '0.1em', margin: '10px 0 11px' }}><span style={{background:'#fff', padding:'0 10px'}}>OR</span></p>
                                                            <input 
                                                                type="text" 
                                                                className="form-control border-primary" 
                                                                name="medicine_name_manually" 
                                                                placeholder="Enter Medicine Name" 
                                                                value={prescriptionFormData?.medicine_name_manually}
                                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, medicine_name_manually: e.target.value })}
                                                            />
                                                            {prescriptionFormError?.med_rx && <div className="text-danger">{prescriptionFormError?.med_rx}</div>}
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Dosage</label>
                                                            <div className="input-group mb-3">
                                                                <input 
                                                                    type="text" 
                                                                    className="form-control border-primary" 
                                                                    name="dosage" 
                                                                    placeholder="Enter Dosage" 
                                                                    value={prescriptionFormData?.med_dosage}
                                                                    onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_dosage: e.target.value })}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Route</label>
                                                            <div className="input-group mb-3">
                                                                <select 
                                                                    className="form-select border-primary" 
                                                                    name="dosage" 
                                                                    value={prescriptionFormData?.med_route}
                                                                    onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_route: e.target.value })}
                                                                >
                                                                    <option value="">Select Route</option>
                                                                    {medsRoute?.map((item) => (
                                                                        <option key={item} value={item}>{item}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Frequency</label>
                                                            <select 
                                                                className="form-select border-primary" 
                                                                name="frequency" 
                                                                value={prescriptionFormData?.med_frequency}
                                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_frequency: e.target.value })}
                                                            >
                                                                <option value="">Select Frequency</option>
                                                                {medsFrequency?.map((item) => (
                                                                    <option key={item} value={item}>{item}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Schedule</label>
                                                            {/* <select 
                                                                className="form-select border-primary d-none" 
                                                                name="schedule" 
                                                                value={prescriptionFormData?.med_schedule}
                                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_schedule: e.target.value })}
                                                            >
                                                                <option value="">Select Schedule</option>
                                                                {medsSchedule?.map((item) => (
                                                                    <option key={item} value={item}>{item}</option>
                                                                ))}
                                                            </select> */}
                                                            <Select
                                                                value={prescriptionFormData.med_schedule_arr || []}
                                                                isMulti={true}
                                                                name="med_schedule"
                                                                options={medsScheduleArr}
                                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_schedule: e.map(i => i.value).join(','), med_schedule_arr: e })}
                                                                className="basic-multi-select"
                                                                classNamePrefix="select"
                                                            />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Instruction</label>
                                                            <div className="input-group mb-3">
                                                                <input 
                                                                    type="text" 
                                                                    className="form-control border-primary" 
                                                                    name="instruction" 
                                                                    placeholder="Enter Instruction" 
                                                                    value={prescriptionFormData?.med_instruction}
                                                                    onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_instruction: e.target.value })}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Duration</label>
                                                            <div className="input-group mb-3">
                                                                <input 
                                                                    type="text" 
                                                                    className="form-control border-primary" 
                                                                    name="duration" 
                                                                    placeholder="Enter Duration" 
                                                                    value={prescriptionFormData?.med_duration}
                                                                    onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, med_duration: e.target.value })}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-footer">
                                                    <button type="button" className="btn btn-primary" onClick={addRxFormData}>{prescriptionFormData?.med_rx_id !== null ? 'Update' : 'Add'}</button>
                                                </div>
                                            </div>
                                            <hr />
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="remarks" className="form-label">REMARKS</label>
                                                <textarea className="form-control" id="remarks" rows="3" value={prescriptionFormData?.remarks} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, remarks: e.target.value })}></textarea>
                                                {prescriptionFormError?.remarks && <div className="text-danger">{prescriptionFormError?.remarks}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="remarks" className="form-label">Appointment Completed</label>
                                                <div className="input-group mb-3">
                                                    <select 
                                                        className="form-select border-primary" 
                                                        name="isCompleted" 
                                                        value={prescriptionFormData?.is_completed}
                                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, is_completed: e.target.value })}
                                                    >
                                                        {['Yes','No'].map((item) => (
                                                            <option key={item} value={item}>{item}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {prescriptionFormError?.is_completed && <div className="text-danger">{prescriptionFormError?.is_completed}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <button type="button" className="btn btn-primary float-end mt-5" onClick={handlePrescription}>Submit</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}